import { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { externalLinkAPI } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px' },
  searchInput: { flex: 1, maxWidth: '300px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b' },
  slugBadge: { fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '4px 8px', borderRadius: '4px' },
  urlText: { fontSize: '13px', color: '#3b82f6', wordBreak: 'break-all', maxWidth: '300px', display: 'inline-block' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'flex', gap: '8px' },
  iconButton: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', transition: 'all 0.15s ease' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyText: { fontSize: '16px', fontWeight: '500', marginBottom: '8px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  button: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primaryButton: { backgroundColor: '#4f46e5', color: '#fff' },
  secondaryButton: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonActive: { backgroundColor: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' }
};

const ExternalLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '', icon: '', sortOrder: 0 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const response = await externalLinkAPI.getAll(params);
      if (response.data.data) {
        setLinks(response.data.data);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch external links');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const openCreateModal = () => {
    setEditingLink(null);
    setFormData({ name: '', url: '', icon: '', sortOrder: 0 });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setFormData({ name: link.name, url: link.url, icon: link.icon || '', sortOrder: link.sortOrder || 0 });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.url) {
      setFormError('Name and URL are required');
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      if (editingLink) {
        await externalLinkAPI.update(editingLink.id, formData);
      } else {
        await externalLinkAPI.create(formData);
      }
      setShowModal(false);
      fetchLinks();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await externalLinkAPI.delete(id);
      fetchLinks();
    } catch (err) {
      setError('Failed to delete link');
    }
  };

  const handleToggle = async (id) => {
    try {
      await externalLinkAPI.toggleStatus(id);
      fetchLinks();
    } catch (err) {
      setError('Failed to toggle status');
    }
  };

  if (loading && links.length === 0) {
    return <div style={styles.container}><p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Loading...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>External Links</h1>
          <p style={styles.subtitle}>Manage sidebar menu links that open in webview in the mobile app</p>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            style={styles.searchInput}
            placeholder="Search links..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <button style={styles.addButton} onClick={openCreateModal}>+ Add Link</button>
        </div>

        {links.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>&#128279;</div>
            <div style={styles.emptyText}>No external links yet</div>
            <p>Click "Add Link" to create your first external link for the mobile sidebar.</p>
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Slug</th>
                  <th style={styles.th}>URL</th>
                  <th style={styles.th}>Icon</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td style={styles.td}>{link.sortOrder}</td>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{link.name}</td>
                    <td style={styles.td}><span style={styles.slugBadge}>{link.slug}</span></td>
                    <td style={styles.td}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" style={styles.urlText}>{link.url}</a>
                    </td>
                    <td style={styles.td}>{link.icon || '-'}</td>
                    <td style={styles.td}>
                      <span
                        style={{ ...styles.statusBadge, ...(link.isActive ? styles.activeStatus : styles.inactiveStatus), cursor: 'pointer' }}
                        onClick={() => handleToggle(link.id)}
                      >
                        {link.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button style={styles.iconButton} onClick={() => openEditModal(link)} title="Edit">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button style={{ ...styles.iconButton, color: '#ef4444' }} onClick={() => handleDelete(link.id)} title="Delete">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <span style={styles.pageInfo}>Showing {links.length} of {total} links</span>
                <div style={styles.pageButtons}>
                  <button
                    style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >Prev</button>
                  <span style={{ ...styles.pageButton, ...styles.pageButtonActive }}>{page}</span>
                  <button
                    style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>{editingLink ? 'Edit Link' : 'Add New Link'}</span>
              <button style={{ ...styles.iconButton, fontSize: '20px' }} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Insurance Calculator"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {formData.name && (
                  <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                    Slug: {formData.name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}
                  </p>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>URL *</label>
                <input
                  style={styles.input}
                  placeholder="https://example.com/page"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Icon (optional)</label>
                <input
                  style={styles.input}
                  placeholder="e.g. link, globe, shield"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Sort Order</label>
                <input
                  style={styles.input}
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: saving ? 0.7 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : (editingLink ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalLinks;
